export const formatToCurrency = (value) => {
    return value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
    });
};

export const formatNumberWithCommas = (number) => {
    return number?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const formatDisplayNumber = (number) => {
    return number?.toString()?.padStart(12,'0')?.replace(/(\d{4})(\d{4})(\d{4})/, '$1-$2-$3');
}

export const formatPercentWithCommas = (number, decimals = 2) => {
    if (isNaN(number)) {
       return "N/A";
    }
    
    // Convert to percentage and format with specified decimals.
    const percentage = (number * 100).toFixed(decimals);
    
    // Use regex to add commas.
    const withCommas = percentage.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    
    // Append the percent symbol.
    return `${withCommas}%`;
};

export const formatToCurrencyExpanded = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol', minimumFractionDigits: 5, maximumFractionDigits: 5 }).format(value);
};

const materialCostPerPound = {
    'stainless-steel': 0.52,
    'aluminum': 0.44,
    'steel': 0.16,
    'copper': 3.1,
};

export const getCustomValueForRow = (row) => {
    const weight = row['manual-weight'] !== null ? row['manual-weight'] : 0;
    return weight * (row['manual-material'] !== null ? materialCostPerPound[row['manual-material']] : 0);
};

export const getValueForRow = (row, selectedCostColumn, selectedQtyColumn, costType, urgency) => {
    const selectedCostValue = row[selectedCostColumn];
    const isCustom = row['manual-type'] === 'custom';

    let valueIfSold = 0;
    if (isCustom) {
        valueIfSold = getCustomValueForRow(row);
    } else if (selectedCostValue) {
        valueIfSold = Number(selectedCostValue?.toString().replace(/[^0-9.]+/g, ''));

        if (costType === 'unit') {
            valueIfSold *= Number(row[selectedQtyColumn]?.toString().replace(/[^0-9.]+/g, '')) || 1;
        }
    }

    const multiple = urgency === 2 ? 1 : urgency === 1 ? 0.82 : 0.64;
    return valueIfSold * multiple;
};

export const getValueForRowFormatted = (row, selectedCostColumn, selectedQtyColumn, costType, urgency) => {
    return formatToCurrency(getValueForRow(row, selectedCostColumn, selectedQtyColumn, costType, urgency));
};

// // ---------------------------------------------------------------------------
// // Tunables ─ tweak these and the rest of the code adapts
// // ---------------------------------------------------------------------------
// const HUE_DIGITS        = [8, 12];               // slice range for hue
// const SAT_DIGITS        = [4,  8];               // slice range for saturation
// const LIGHT_DIGITS      = [0,  4];               // slice range for lightness

// const BASE_SAT_MIN      = 60;                    // 10-35  => 26-point span
// const BASE_SAT_SPAN     = 10;

// const BASE_LIGHT_MIN    = 20;                    // 22-42  => 21-point span
// const BASE_LIGHT_SPAN   = 24;

// // “Pastel-ising” factors
// export const PASTEL_SAT_FACTOR = 0.6;                  // keep 25 % of the base sat
// export const PASTEL_SAT_MIN    = 4;                     // never drop below 4 %

// export const PASTEL_LIGHT_ADD  = 70;                    // push lightness up by 55 %
// export const PASTEL_LIGHT_MAX  = 90;                    // but cap at 96 %

// // ---------------------------------------------------------------------------
// // Utility
// // ---------------------------------------------------------------------------
// export const colorFromShipment = (num) => {
//     const str   = String(num).padStart(12, "0");

//     const hue   = parseInt(str.slice(...HUE_DIGITS),   10) % 360;
//     const sat   = BASE_SAT_MIN   +
//                     (parseInt(str.slice(...SAT_DIGITS),  10) % BASE_SAT_SPAN);
//     const light = BASE_LIGHT_MIN +
//                     (parseInt(str.slice(...LIGHT_DIGITS),10) % BASE_LIGHT_SPAN);

//     return { hue, sat, light };
// };

// 1) Hue bands you actually want to see
//    Feel free to tweak or add more bands.
const HUE_RANGES = [
  [  0,  45],   // reds / oranges
  [165, 250]   // green / blue
];

// 2) Map an arbitrary integer to one of those allowed bands
function mapToHueRanges(value) {
  // total usable size
  const span = HUE_RANGES.reduce((sum, [a, b]) => sum + (b - a + 1), 0);

  // wrap value so it falls somewhere inside that span
  let n = value % span;

  for (const [start, end] of HUE_RANGES) {
    const len = end - start + 1;
    if (n < len) return start + n;   // landed in this band
    n -= len;                        // otherwise skip ahead
  }
  return HUE_RANGES[0][0];           // fallback (should never hit)
}

// 3) Clamp saturation & lightness so no pastels appear
const BASE_SAT_MIN    = 60;  // vivid enough
const BASE_SAT_SPAN   = 30;  // 60-89
const BASE_LIGHT_MIN  = 10;  // mid-tones
const BASE_LIGHT_SPAN = 20;  // 30-54

// These are the digit slices you already use
const HUE_DIGITS   = [0, 4];
const SAT_DIGITS   = [4, 8];
const LIGHT_DIGITS = [8,12];

// ------------------------------------------------------------------
export const colorFromNumber = (num) => {
  const s = String(num).padStart(12, '0');

  const hueRaw   = parseInt(s.slice(...HUE_DIGITS),   10);
  const satRaw   = parseInt(s.slice(...SAT_DIGITS),   10);
  const lightRaw = parseInt(s.slice(...LIGHT_DIGITS), 10);

  const hue   = mapToHueRanges(hueRaw);                        // 0-359 but only in allowed bands
  const sat   = BASE_SAT_MIN   + (satRaw   % BASE_SAT_SPAN);   // 60-89
  const light = BASE_LIGHT_MIN + (lightRaw % BASE_LIGHT_SPAN); // 30-54

  return { hue, sat, light };  // e.g. {hue: 212, sat: 68, light: 44}
};

// ---------------------------------------------------------------------------
//  NEW: translate that 12-digit number into a colour image filter
// ---------------------------------------------------------------------------
export const filterFromNumber = (num) => {
    const { hue, sat, light } = colorFromNumber(num);

    // 1 = “no-change” for saturate()/brightness().
    // We want a nice spread around 1, never getting absurd values.
    const saturate  = 0.4 + (sat   / 100) * 1.6;   // ≈ 0.48-≈0.64  →  1.0-≈2.0
    const brightness = 0.5 + (light / 100) * 1.0;  // ≈ 0.7-1.0  →  1.2-1.5

    // Order matters: hue-rotate → saturate → brightness
    return `hue-rotate(${hue}deg) saturate(${saturate}) brightness(${brightness})`;
};

export const expiresInTextFromDate = (utcExpiration) => {
  //---------------------------------------------------------------------------
  // 1. Normalise the *UTC* value into a Date instance
  //---------------------------------------------------------------------------
  let expUTC;

  if (utcExpiration instanceof Date) {
    expUTC = utcExpiration;                             // already a Date
  } else if (typeof utcExpiration === 'number') {       // millis since epoch
    expUTC = new Date(utcExpiration);
  } else if (typeof utcExpiration === 'string') {       // ISO text
    // If the string has no zone info, add “Z” so it is parsed as UTC.
    const iso = /[Z+-]\d\d:?(\d\d)?$/.test(utcExpiration)
      ? utcExpiration
      : `${utcExpiration}Z`;
    expUTC = new Date(iso);
  } else {
    throw new TypeError('Unsupported date format');
  }

  //---------------------------------------------------------------------------
  // 2. Convert that UTC instant to a local Date.
  //---------------------------------------------------------------------------
  const expLocal = new Date(expUTC.getTime()); // same instant, local view
  const now      = new Date();                 // current local time

  // --------------------------------------------------------------------------
  // EXPIRED  →  "MM/DD/YYYY at HH:MMam/pm"
  // --------------------------------------------------------------------------
  if (expLocal <= now) {
    const pad2 = (n) => String(n).padStart(2, '0');

    const month   = pad2(expLocal.getMonth() + 1);  // 1-12 → 01-12
    const day     = pad2(expLocal.getDate());       // 1-31 → 01-31
    const year    = expLocal.getFullYear();

    const minutes = pad2(expLocal.getMinutes());
    let   hours   = expLocal.getHours();            // 0-23
    const ampm    = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;                       // 0,12 → 12-hour clock

    return `Expired: ${month}/${day}/${year} at ${hours}:${minutes}${ampm}`;
  }

  //---------------------------------------------------------------------------
  // 3. Compute the remaining time and format it
  //---------------------------------------------------------------------------
  const diffMs       = expLocal - now;             // milliseconds
  const totalMinutes = Math.floor(diffMs / 60_000);

  const days    = Math.floor(totalMinutes / (24 * 60));
  const hours   = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (days   > 0) parts.push(`${days} day${days   > 1 ? 's' : ''}`);
  if (hours  > 0) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);

  // Only show minutes when < 1 day left
  if (days === 0 && minutes > 0) {
    parts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);
  }

  if (!parts.length) {
    return 'less than 1 minute';
  }

  // Nice English joining: “a, b and c”
  return "Expires: " + (parts.length === 1
    ? parts[0]
    : parts.length === 2
      ? `${parts[0]} and ${parts[1]}`
      : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`);
};